import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Modal, Space, QRCode, Form, Input, Dropdown, Avatar, Typography, Select, Tooltip } from 'antd';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import styles from './MainLayout.module.css';
import { useAuth } from '../../contexts/AuthContext';
import LoginModal from '../LoginModal';
import LoginButton from '../LoginButton/LoginButton';
import { message } from 'antd';
import { ROUTES } from '../../constants';
import WxLoginModal from '../WxLoginModal';
import { UserOutlined, DownOutlined, ReloadOutlined, SwapOutlined } from '@ant-design/icons';
import logo from '../../assets/logo.png';
import qrcodeImage from '../../assets/qrcode_certexam.jpg';
import miniProgramQrcode from '../../assets/xcx.jpg';
import axios from 'axios';
import api from '../../utils/api';

const { Content, Footer } = Layout;
const { Option } = Select;

const privateRoutes = [
  ROUTES.USER,
  ROUTES.CATEGORY,
  ROUTES.WRONG_QUESTIONS.ROOT
];

const publicRoutes = [
  ROUTES.HOME,
  ROUTES.STUDY.ROOT,
  ROUTES.TIKU.ROOT,
  ROUTES.EXAM.ROOT
];

const MainLayout = () => {
  const { user, login, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [loginVisible, setLoginVisible] = useState(false);
  const [wxLoginVisible, setWxLoginVisible] = useState(false);
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [defaultCategory, setDefaultCategory] = useState(null);
  const [showQrCode, setShowQrCode] = useState(false);
  const [showMiniProgramQrCode, setShowMiniProgramQrCode] = useState(false);

  // 获取默认分类
  useEffect(() => {
    const loadDefaultCategory = () => {
      const lastSelected = localStorage.getItem('lastSelectedCategory');
      if (lastSelected) {
        try {
          setDefaultCategory(JSON.parse(lastSelected));
        } catch (error) {
          console.error('解析默认分类失败', error);
          localStorage.removeItem('lastSelectedCategory');
        }
      } else {
        setDefaultCategory(null);
      }
    };

    // 初始加载
    loadDefaultCategory();

    // 监听分类变更事件
    const handleCategoryChange = (event) => {
      setDefaultCategory(event.detail);
    };

    window.addEventListener('categoryChanged', handleCategoryChange);

    // 清理函数
    return () => {
      window.removeEventListener('categoryChanged', handleCategoryChange);
    };
  }, []);

  // 重置默认分类
  const resetDefaultCategory = () => {
    localStorage.removeItem('lastSelectedCategory');
    setDefaultCategory(null);
    message.success('已重置专业分类，请重新选择');
    navigate(ROUTES.TIKU.ROOT);
  };

  // 专业切换下拉菜单
  const professionMenuItems = {
    items: [
      {
        key: 'reset',
        label: '重置',
        icon: <ReloadOutlined style={{ fontSize: '14px' }} />,
        onClick: resetDefaultCategory,
        style: {
          fontSize: '14px',
          padding: '10px 16px',
          background: '#1a1a1a',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '8px',
          margin: '4px 0',
          border: '1px solid #333',
          transition: 'all 0.3s ease'
        }
      }
    ],
    style: {
      background: '#1a1a1a',
      borderRadius: '12px',
      padding: '8px',
      minWidth: '120px',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
      border: '1px solid #333'
    }
  };

  const menuItems = [
    {
      key: ROUTES.HOME,
      label: '职考助手',
      onClick: () => navigate(ROUTES.HOME)
    },
    {
      key: ROUTES.STUDY.ROOT,
      label: '知识卡',
      onClick: () => navigate(ROUTES.STUDY.ROOT)
    },
    {
      key: ROUTES.CATEGORY,
      label: '试题查询',
      onClick: () => navigate(ROUTES.CATEGORY)
    },
    {
      key: ROUTES.USER,
      label: '学习中心',
      onClick: () => {
        if (user) {
          navigate(ROUTES.USER);
        } else {
          message.warning('请先登录后访问学习中心');
          setLoginVisible(true);
        }
      }
    },
    {
      key: ROUTES.TIKU.ROOT,
      label: (
        <Dropdown
          menu={professionMenuItems}
          placement="bottomCenter"
          trigger={['hover']}
        >
          <span className={styles.specialMenuItem}>
            {defaultCategory ? (
              defaultCategory.subCategoryName.length > 6
                ? `${defaultCategory.subCategoryName.substring(0, 6)}`
                : defaultCategory.subCategoryName
            ) : '选择专业'}
          </span>
        </Dropdown>
      ),
      className: styles.specialMenuItemContainer,
      onClick: () => {
        navigate(ROUTES.TIKU.ROOT);
      }
    }
  ];

  const userMenuItems = [
    {
      key: 'study',
      label: '学习中心',
      onClick: () => navigate('/user')
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      label: '退出登录',
      onClick: () => {
        logout();
        navigate('/');
      }
    }
  ];

  const userMenuConfig = {
    items: userMenuItems
  };

  const showLoginModal = () => {
    setLoginVisible(true);
  };

  const handleLogin = async (values) => {
    try {
      await login(values.email, values.password);
      message.success('登录成功');
      setLoginVisible(false);
      setShowPasswordLogin(false);
      navigate('/user');
    } catch (error) {
      console.error('Login error:', error);
      message.error(error.response?.data?.message || '登录失败');
    }
  };

  const handleLoginCancel = () => {
    setLoginVisible(false);
    setShowPasswordLogin(false);
  };

  const showWxLoginModal = () => {
    setWxLoginVisible(true);
  };

  const handleWxLoginCancel = () => {
    setWxLoginVisible(false);
  };

  const handlePasswordLogin = () => {
    setShowPasswordLogin(true);
  };

  return (
    <Layout className={styles.layout}>
      <Layout.Header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.logo}>
            <Link to="/">
              <img src={logo} alt="logo" className={styles.logoImage} />
            </Link>
          </div>
          <div className={styles.navContainer}>
            <Menu
              mode="horizontal"
              selectedKeys={[location.pathname]}
              items={menuItems}
              className={styles.menu}
            />
          </div>
          <div className={styles.userArea}>
            {user ? (
              <Dropdown
                menu={userMenuConfig}
                placement="bottomRight"
                trigger={['hover']}
              >
                <div className={styles.userInfo}>
                  <Avatar
                    icon={<UserOutlined />}
                    src={user.avatar}
                    size={32}
                  />
                </div>
              </Dropdown>
            ) : (
              <Button type="primary" onClick={showLoginModal}>
                登录
              </Button>
            )}
          </div>
        </div>
      </Layout.Header>

      <Content className={styles.content}>
        <div className={styles.container}>
          <Outlet />
        </div>
      </Content>

      <Footer className={styles.footer}>
        <div className={styles.footerContent}>
          <Typography.Link
            href="/exam"
            className={styles.footerLink}
          >
            系统介绍
          </Typography.Link>
          <Typography.Link
            href="/game/accounting"
            className={styles.footerLink}
          >
            趣味会计
          </Typography.Link>
          <Tooltip
            title={
              <div className={styles.qrCodeContainer}>
                <img src={qrcodeImage} alt="公众号二维码" width={120} />
                <div className={styles.qrCodeText}>扫码关注公众号</div>
              </div>
            }
            color="#1a1a1a"
            placement="top"
            overlayInnerStyle={{ padding: '16px' }}
          >
            <Typography.Link
              className={styles.footerLink}
              onMouseEnter={() => setShowQrCode(true)}
              onMouseLeave={() => setShowQrCode(false)}
            >
              公众号
            </Typography.Link>
          </Tooltip>
          <Tooltip
            title={
              <div className={styles.qrCodeContainer}>
                <img src={miniProgramQrcode} alt="小程序二维码" width={120} />
                <div className={styles.qrCodeText}>扫码使用小程序</div>
              </div>
            }
            color="#1a1a1a"
            placement="top"
            overlayInnerStyle={{ padding: '16px' }}
          >
            <Typography.Link
              className={styles.footerLink}
              onMouseEnter={() => setShowMiniProgramQrCode(true)}
              onMouseLeave={() => setShowMiniProgramQrCode(false)}
            >
              小程序
            </Typography.Link>
          </Tooltip>
          <Typography.Link
            href="/sitemap.html"
            className={styles.footerLink}
          >
            考点笔记
          </Typography.Link>
        </div>
        <Typography.Text className={styles.copyright}>
          沪ICP备19041435号-7 增值电信许可证：沪B2-20220746 ©2025 职考助手 版权所有
        </Typography.Text>
      </Footer>

      <LoginModal
        visible={loginVisible && showPasswordLogin}
        onCancel={handleLoginCancel}
      >
        <Form
          name="login"
          onFinish={handleLogin}
          initialValues={{
            email: 'test@example.com',
            password: 'test123'
          }}
        >
          <Form.Item
            name="email"
            rules={[{ required: true, message: '请输入邮箱' }]}
          >
            <Input placeholder="邮箱：test@example.com" />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password placeholder="密码：test123" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              登录
            </Button>
          </Form.Item>
        </Form>
      </LoginModal>

      <WxLoginModal
        visible={loginVisible && !showPasswordLogin}
        onCancel={handleLoginCancel}
      />
    </Layout>
  );
};

export default MainLayout;